/**
 * campos.tsx — componentes de campo do formulário do DFD, com o visual do
 * new_dfd.html original (caixa slate-50, label com ícone de varinha nos
 * campos sugeríveis, foco verde).
 */

import { faMagic, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactNode } from 'react'

export const CLASSE_INPUT =
  'w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 ' +
  'focus:border-transparent transition-all dark:border-dark-border dark:bg-dark-bg dark:text-slate-100'

export function Rotulo({
  htmlFor, children, obrigatorio, sugerivel,
}: {
  htmlFor: string
  children: ReactNode
  obrigatorio?: boolean
  sugerivel?: boolean
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200"
    >
      {children}
      {obrigatorio && <span className="ml-0.5 text-red-500">*</span>}
      {sugerivel && (
        <FontAwesomeIcon
          icon={faMagic}
          title="Sugestão Inteligente com IA ao focar o campo vazio"
          className="ml-2 text-indigo-500"
        />
      )}
    </label>
  )
}

export function CampoTexto({
  nome, rotulo, valor, aoMudar, obrigatorio, placeholder, tipo = 'text',
  maxLength, erro, aoSair,
}: {
  nome: string
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  obrigatorio?: boolean
  placeholder?: string
  tipo?: string
  maxLength?: number
  erro?: string
  aoSair?: () => void
}) {
  return (
    <div>
      <Rotulo htmlFor={nome} obrigatorio={obrigatorio}>{rotulo}</Rotulo>
      <input
        id={nome}
        name={nome}
        type={tipo}
        value={valor}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => aoMudar(e.target.value)}
        onBlur={aoSair}
        className={`${CLASSE_INPUT} ${erro ? 'border-red-400 ring-1 ring-red-300' : ''}`}
      />
      {erro && <p className="mt-1 text-xs text-red-600">{erro}</p>}
    </div>
  )
}

export function CampoTextarea({
  nome, rotulo, valor, aoMudar, obrigatorio, placeholder, linhas = 4,
  sugerivel, aoFocar, sugerindo, erro, aoSair,
}: {
  nome: string
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  obrigatorio?: boolean
  placeholder?: string
  linhas?: number
  sugerivel?: boolean
  aoFocar?: () => void
  sugerindo?: boolean
  erro?: string
  aoSair?: () => void
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-dark-bg/40">
      <div className="flex items-center justify-between">
        <Rotulo htmlFor={nome} obrigatorio={obrigatorio} sugerivel={sugerivel}>
          {rotulo}
        </Rotulo>
        {sugerindo && (
          <span className="mb-2 text-xs text-indigo-600">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
            Gerando sugestão…
          </span>
        )}
      </div>
      <textarea
        id={nome}
        name={nome}
        rows={linhas}
        value={valor}
        placeholder={placeholder}
        onFocus={aoFocar}
        onBlur={aoSair}
        onChange={(e) => aoMudar(e.target.value)}
        className={`${CLASSE_INPUT} ${erro ? 'border-red-400 ring-1 ring-red-300' : ''}`}
      />
      {erro && <p className="mt-1 text-xs text-red-600">{erro}</p>}
    </div>
  )
}

export function CampoSelect({
  nome, rotulo, valor, aoMudar, opcoes, obrigatorio, vazio = 'Selecione...', erro,
  desabilitado, ajuda,
}: {
  nome: string
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  opcoes: { valor: string; rotulo: string }[]
  obrigatorio?: boolean
  vazio?: string
  erro?: string
  /** Trava o campo quando o valor é consequência de outra escolha (ex.:
   *  Art. 79, II impõe livre escolha do beneficiário). O form é POST
   *  nativo e select desabilitado NÃO serializa — o hidden mantém o
   *  valor no envio. */
  desabilitado?: boolean
  ajuda?: string
}) {
  return (
    <div>
      <Rotulo htmlFor={nome} obrigatorio={obrigatorio}>{rotulo}</Rotulo>
      <select
        id={nome}
        name={desabilitado ? undefined : nome}
        value={valor}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.value)}
        className={`${CLASSE_INPUT} ${erro ? 'border-red-400 ring-1 ring-red-300' : ''} ${desabilitado ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <option value="">{vazio}</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>{o.rotulo}</option>
        ))}
      </select>
      {desabilitado && <input type="hidden" name={nome} value={valor} />}
      {ajuda && !erro && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{ajuda}</p>
      )}
      {erro && <p className="mt-1 text-xs text-red-600">{erro}</p>}
    </div>
  )
}

/** Título de seção — mesmo padrão do template (borda inferior + ícone). */
export function TituloSecao({
  icone, cor, children,
}: {
  icone: import('@fortawesome/fontawesome-svg-core').IconDefinition
  cor: string
  children: ReactNode
}) {
  return (
    <h4 className={`mb-6 border-b border-slate-200 pb-3 text-xl font-semibold dark:border-slate-700 ${cor}`}>
      <FontAwesomeIcon icon={icone} className="mr-2" />
      {children}
    </h4>
  )
}
